import { Alert, AlertDescription, AlertIcon, Box, Text, VStack } from '@chakra-ui/react'
import { useMemo } from 'react'
import type { ChildTicketTask } from '@/lib/services/childTicketService'
import { getModule, resolveModuleForField } from './modules/registry'
import { dependenciesMet, resolveTaskFormFields } from './modules/resolveFormFields'
import type { TaskFormFieldDefinition } from './modules/types'
import '@/components/helpdesk/TicketDetail/tasks/modules/index'

type Props = {
  task: ChildTicketTask
  parentTicketId: string
  formData: Record<string, unknown>
  onFormDataChange: (key: string, value: unknown) => void
  ticketPropertiesProps?: Record<string, unknown>
  isReadOnly?: boolean
  onRefresh?: () => void | Promise<void>
}

export default function TaskFormRenderer({
  task,
  parentTicketId,
  formData,
  onFormDataChange,
  ticketPropertiesProps,
  isReadOnly,
  onRefresh,
}: Props) {
  const fields = useMemo(() => resolveTaskFormFields(task), [task])
  const storeId = ticketPropertiesProps?.storeId as number | null | undefined

  if (fields.length === 0) {
    return null
  }

  return (
    <Box mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" mb={3}>
        Task operations
      </Text>
      <VStack align="stretch" spacing={4}>
        {fields.map((field) => (
          <TaskFormFieldRow
            key={field.key}
            field={field}
            fields={fields}
            task={task}
            parentTicketId={parentTicketId}
            formData={formData}
            onFormDataChange={onFormDataChange}
            ticketPropertiesProps={ticketPropertiesProps}
            isReadOnly={isReadOnly}
            onRefresh={onRefresh}
            storeId={storeId}
          />
        ))}
      </VStack>
    </Box>
  )
}

function TaskFormFieldRow({
  field,
  fields,
  task,
  parentTicketId,
  formData,
  onFormDataChange,
  ticketPropertiesProps,
  isReadOnly,
  onRefresh,
  storeId,
}: {
  field: TaskFormFieldDefinition
  fields: TaskFormFieldDefinition[]
  task: ChildTicketTask
  parentTicketId: string
  formData: Record<string, unknown>
  onFormDataChange: (key: string, value: unknown) => void
  ticketPropertiesProps?: Record<string, unknown>
  isReadOnly?: boolean
  onRefresh?: () => void | Promise<void>
  storeId?: number | null
}) {
  const mod = resolveModuleForField(field) ?? getModule(field.module)
  const valuesByKey = formData
  const dep = dependenciesMet(field, valuesByKey, storeId, fields)
  const ctx = {
    field,
    task,
    parentTicketId,
    formData,
    onFormDataChange,
    ticketPropertiesProps,
    isReadOnly,
    onRefresh,
    allFields: fields,
    valuesByKey,
    storeId,
  }

  if (!mod) {
    return (
      <Alert status="warning" size="sm">
        <AlertIcon />
        <AlertDescription fontSize="sm">Unknown module: {field.module}</AlertDescription>
      </Alert>
    )
  }

  const blocked = mod.isBlocked?.(ctx)
  if (!dep.ok || blocked?.blocked) {
    return (
      <Alert status="info" size="sm">
        <AlertIcon />
        <AlertDescription fontSize="sm">{blocked?.message ?? dep.message}</AlertDescription>
      </Alert>
    )
  }

  if (field.module === 'core.assignee') {
    return null
  }

  const Component = mod.component
  return (
    <Component
      field={field}
      task={task}
      parentTicketId={parentTicketId}
      formData={formData}
      onFormDataChange={onFormDataChange}
      ticketPropertiesProps={ticketPropertiesProps}
      isReadOnly={isReadOnly}
      onRefresh={onRefresh}
    />
  )
}
